import { z } from 'zod';
import { handleStoresPull } from '../handlers/pull.handler.js';
import {
  handleListStores,
  handleGetStoreInfo,
  handleCreateStore,
  handleIndexStore,
  handleDeleteStore,
  handleCheckModels,
  handleStoreHealth,
} from '../handlers/store.handler.js';
import type { CommandDefinition } from './registry.js';
import type { StoreHealthArgs } from '../handlers/store.handler.js';
import type {
  ListStoresArgs,
  GetStoreInfoArgs,
  CreateStoreArgs,
  IndexStoreArgs,
  DeleteStoreArgs,
} from '../schemas/index.js';
import type { HandlerContext, ToolResponse } from '../types.js';

/**
 * Store management commands for the execute meta-tool
 *
 * These commands wrap the existing store handlers, providing
 * a unified interface through the execute command.
 *
 * Note: Type assertions are necessary here because CommandHandler uses
 * Record<string, unknown> for generic command args, while handlers expect
 * specific typed args. Zod validates at runtime before the cast.
 */
/* eslint-disable @typescript-eslint/consistent-type-assertions */
export const storeCommands: CommandDefinition[] = [
  {
    name: 'stores',
    description: 'List all indexed knowledge stores',
    argsSchema: z.object({
      type: z.enum(['file', 'repo', 'web']).optional().describe('Filter by store type'),
    }),
    handler: (args, context) => handleListStores(args as unknown as ListStoresArgs, context),
  },
  {
    name: 'store:info',
    description: 'Get detailed information about a specific store',
    argsSchema: z.object({
      store: z.string().min(1).describe('Store name or ID'),
    }),
    handler: (args, context) => handleGetStoreInfo(args as unknown as GetStoreInfoArgs, context),
  },
  {
    name: 'store:create',
    description: 'Create a new knowledge store from git URL, local path, or web URL',
    argsSchema: z.object({
      name: z.string().min(1).describe('Store name'),
      type: z.enum(['file', 'repo', 'web']).describe('Store type'),
      source: z.string().min(1).describe('Git URL, local path, or web URL'),
      branch: z.string().optional().describe('Git branch (for repo type)'),
      description: z.string().optional().describe('Store description'),
      tags: z.array(z.string()).optional().describe('Tags for categorizing the store'),
      depth: z.number().int().positive().optional().describe('Git clone depth (for repo type)'),
      maxPages: z
        .number()
        .int()
        .positive()
        .optional()
        .describe('Maximum pages to crawl (for web type)'),
      crawlInstructions: z.string().optional().describe('Instructions for crawler (for web type)'),
      extractInstructions: z
        .string()
        .optional()
        .describe('Instructions for content extraction (for web type)'),
    }),
    handler: (args, context) => handleCreateStore(args as unknown as CreateStoreArgs, context),
  },
  {
    name: 'store:index',
    description: 'Re-index a knowledge store to update search data',
    argsSchema: z.object({
      store: z.string().min(1).describe('Store name or ID'),
    }),
    handler: (args, context) => handleIndexStore(args as unknown as IndexStoreArgs, context),
  },
  {
    name: 'store:delete',
    description: 'Delete a knowledge store and all associated data',
    argsSchema: z.object({
      store: z.string().min(1).describe('Store name or ID'),
    }),
    handler: (args, context) => handleDeleteStore(args as unknown as DeleteStoreArgs, context),
  },
  {
    name: 'stores:pull',
    description: 'Pull latest changes from git remote and re-index a repo-type store',
    argsSchema: z.object({
      store: z.string().min(1).describe('Store name or ID'),
    }),
    handler: (args: Record<string, unknown>, context: HandlerContext): Promise<ToolResponse> => {
      if (typeof args['store'] !== 'string' || args['store'].length === 0) {
        throw new Error('Store name or ID is required');
      }
      return handleStoresPull({ store: args['store'] }, context);
    },
  },
  {
    name: 'stores:check-models',
    description: 'Check embedding model compatibility for all stores',
    argsSchema: z.object({}),
    handler: (_args, context) => handleCheckModels({}, context),
  },
  {
    name: 'stores:health',
    description: 'Check health of stores: path existence, model compatibility',
    argsSchema: z.object({
      store: z.string().optional().describe('Check a specific store only (name or ID)'),
    }),
    handler: (args: Record<string, unknown>, context: HandlerContext): Promise<ToolResponse> => {
      const typedArgs: StoreHealthArgs =
        typeof args['store'] === 'string' ? { store: args['store'] } : {};
      return handleStoreHealth(typedArgs, context);
    },
  },
];
/* eslint-enable @typescript-eslint/consistent-type-assertions */
